import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Spinner, Alert, ProgressBar, Form, InputGroup, Button, ButtonGroup, Badge, Modal } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import ReactDOM from 'react-dom';
import projectService from '../../services/projectService';
import reportService from '../../services/reportService';
import { formatHoursToHoursMinutes, formatDecimalHours } from '../../utils/timeUtils';
import ProjectFiles from '../../components/admin/ProjectFiles';
import ProjectUrls from '../../components/admin/ProjectUrls';
// import 'bootstrap-icons/font/bootstrap-icons.css';

const ProjectHours = () => {
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [projectsWithHours, setProjectsWithHours] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedRole, setSelectedRole] = useState(''); // For role-based filtering
  const [expandedProjects, setExpandedProjects] = useState({});
  const [dropdownSearch, setDropdownSearch] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Fetch all projects on component mount
  useEffect(() => {
    const fetchData = async () => {
      await fetchProjects();
    };
    
    fetchData();
  }, []);

  // Handle navigation state from global search
  useEffect(() => {
    if (location.state?.selectedProjectId) {
      setSelectedProject(location.state.selectedProjectId.toString());
      if (location.state?.searchTerm) {
        setDropdownSearch(location.state.searchTerm);
      }
    }
  }, [location.state]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen) {
        const dropdownElement = document.querySelector('[data-dropdown-portal]');
        const triggerElement = event.target.closest('.form-control');
        
        if (!dropdownElement?.contains(event.target) && !triggerElement) {
          setIsDropdownOpen(false);
          setDropdownSearch('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);
  
  // Filter projects when selected project or selected role changes
  useEffect(() => {
    if (!projectsWithHours.length) return;
    
    let filtered = [...projectsWithHours];
    
    // Filter by selected project
    if (selectedProject) {
      filtered = filtered.filter(project => project.id === parseInt(selectedProject));
    }
    
    setFilteredProjects(filtered);
  }, [selectedProject, projectsWithHours]);

  // Fetch all projects
  const fetchProjects = async () => {
    setLoading(true);
    setError('');
    try {
      // Get all projects
      const projectsData = await projectService.getAllProjects();
      setProjects(projectsData);
      
      // Fetch hours for each project with role filtering if selected
      const projectsWithHoursData = await Promise.all(
        projectsData.map(async (project) => {
          try {
            const hoursData = await reportService.getProjectHours(project.id, selectedRole || null);
            return {
              ...project,
              hoursData
            };
          } catch (err) {
            console.error(`Error fetching hours for project ${project.id}:`, err);
            return {
              ...project,
              hoursData: {
                totalHours: 0,
                userDistribution: [],
                taskCount: 0
              }
            };
          }
        })
      );
      
      setProjectsWithHours(projectsWithHoursData);
      setFilteredProjects(projectsWithHoursData);
      
      // Reset project selection but keep role selection
      setSelectedProject('');
      setDropdownSearch('');
    } catch (err) {
      setError('Failed to load projects. Please try again.');
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch projects with role filter
  const fetchProjectsByRole = async (role) => {
    setSelectedRole(role);
    setLoading(true);
    setError('');
    
    try {
      // Get all projects
      const projectsData = await projectService.getAllProjects();
      
      // Fetch hours for each project with role filtering
      const projectsWithHoursData = await Promise.all(
        projectsData.map(async (project) => {
          try {
            const hoursData = await reportService.getProjectHours(project.id, role);
            return {
              ...project,
              hoursData
            };
          } catch (err) {
            console.error(`Error fetching hours for project ${project.id} with role ${role}:`, err);
            return {
              ...project,
              hoursData: {
                totalHours: 0,
                userDistribution: [],
                taskCount: 0
              }
            };
          }
        })
      );
      
      setProjectsWithHours(projectsWithHoursData);
      setFilteredProjects(projectsWithHoursData);
    } catch (err) {
      setError(`Failed to load projects filtered by ${role}. Please try again.`);
      console.error(`Error fetching projects by role ${role}:`, err);
    } finally {
      setLoading(false);
    }
  };

  // Using the imported formatting functions from utils/timeUtils.js

  // Get different colors for progress bars
  const getProgressBarVariant = (index) => {
    const variants = ['primary', 'success', 'info', 'warning', 'danger', 'secondary'];
    return variants[index % variants.length];
  };

  // Handle dropdown search change
  const handleDropdownSearchChange = (e) => {
    setDropdownSearch(e.target.value);
  };
  
  // Handle project selection from searchable dropdown
  const handleProjectSelect = (projectId) => {
    setSelectedProject(projectId);
    setIsDropdownOpen(false);
    setDropdownSearch('');
  };
  
  // Get filtered projects for dropdown
  const getFilteredDropdownProjects = () => {
    if (!dropdownSearch) return projects;
    return projects.filter(project => 
      project.name.toLowerCase().includes(dropdownSearch.toLowerCase())
    );
  };
  
  // Get selected project name
  const getSelectedProjectName = () => {
    if (!selectedProject) return 'Select a project...';
    const project = projects.find(p => p.id === parseInt(selectedProject));
    return project ? project.name : 'Select a project...';
  };
  
  // Clear filters
  const handleClearFilters = () => {
    setSelectedProject('');
    setSelectedRole('');
    setDropdownSearch('');
    fetchProjects();
  };
  
  // Handle role selection
  const handleRoleSelect = (role) => {
    if (selectedRole === role) {
      // If clicking the same role, clear the filter
      setSelectedRole('');
      fetchProjects();
    } else {
      // Apply the new role filter
      fetchProjectsByRole(role);
    }
  };

  const toggleProjectExpand = (projectId) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };
  
  return (
    <div className="page-fade-in">
    <Container fluid className="mt-4">
      {error && <Alert variant="danger">{error}</Alert>}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <>
          {/* Search and Filter */}
          <Card className="mb-4 shadow-sm" style={{ border: 'none', borderRadius: 16, position: 'sticky', top: '75px', zIndex: 900 }}>
            <Card.Body style={{ padding: '1.5rem' }}>
              <Row className="g-3 align-items-end">
                <Col lg={6} md={12}>
                  <div className="position-static">
                    <label className="form-label fw-semibold mb-2" style={{ color: '#495057', fontSize: '0.9rem' }}>
                      <i className="bi bi-folder2-open me-2"></i>Select Project
                    </label>
                    <div className="position-static">
                      <div 
                        className="position-relative"
                        data-dropdown-trigger="true"
                        style={{ position: 'relative' }}
                      >
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Type to search or select a project..."
                          value={dropdownSearch}
                          onChange={(e) => {
                            setDropdownSearch(e.target.value);
                            setIsDropdownOpen(true);
                          }}
                          onFocus={() => setIsDropdownOpen(true)}
                          style={{ 
                            padding: '0.75rem 2.5rem 0.75rem 1rem',
                            border: '2px solid #e9ecef',
                            borderRadius: 10,
                            transition: 'all 0.2s ease',
                            backgroundColor: '#fff',
                            fontSize: '0.95rem'
                          }}
                          onMouseEnter={(e) => e.target.style.borderColor = '#7C6FA0'}
                          onMouseLeave={(e) => e.target.style.borderColor = '#e9ecef'}
                        />
                        <i 
                          className={`bi bi-chevron-${isDropdownOpen ? 'up' : 'down'}`} 
                          style={{ 
                            position: 'absolute',
                            right: '1rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#7C6FA0',
                            pointerEvents: 'none'
                          }}
                        ></i>
                        {selectedProject && dropdownSearch === '' && (
                          <span 
                            style={{ 
                              position: 'absolute',
                              left: '1rem',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: '#212529',
                              fontSize: '0.95rem',
                              pointerEvents: 'none'
                            }}
                          >
                            {getSelectedProjectName()}
                          </span>
                        )}
                      </div>
                      
                      {isDropdownOpen && (
                        ReactDOM.createPortal(
                          <div 
                            className="bg-white border rounded shadow" 
                            data-dropdown-portal="true"
                            style={{ 
                              position: 'fixed',
                              zIndex: 1050,
                              maxHeight: '300px',
                              overflowY: 'auto',
                              minWidth: '250px',
                              maxWidth: '500px',
                              borderRadius: 12,
                              border: '1px solid #dee2e6'
                            }}
                            ref={(el) => {
                              if (el) {
                                const trigger = document.querySelector('[data-dropdown-trigger]');
                                if (trigger) {
                                  const rect = trigger.getBoundingClientRect();
                                  el.style.top = `${rect.bottom + window.scrollY + 5}px`;
                                  el.style.left = `${rect.left + window.scrollX}px`;
                                  el.style.width = `${rect.width}px`;
                                }
                              }
                            }}
                          >
                            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                              {getFilteredDropdownProjects().length > 0 ? (
                                getFilteredDropdownProjects().map((project, index) => (
                                  <div
                                    key={project.id}
                                    className="px-3 py-2"
                                    style={{ 
                                      cursor: 'pointer',
                                      transition: 'all 0.15s ease',
                                      borderBottom: index < getFilteredDropdownProjects().length - 1 ? '1px solid #f1f3f5' : 'none',
                                      fontSize: '0.9rem'
                                    }}
                                    onClick={() => handleProjectSelect(project.id)}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = '#f8f9fa';
                                      e.target.style.paddingLeft = '1.25rem';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = 'transparent';
                                      e.target.style.paddingLeft = '1rem';
                                    }}
                                  >
                                    <i className="bi bi-folder me-2" style={{ color: '#7C6FA0' }}></i>
                                    {project.name}
                                  </div>
                                ))
                              ) : (
                                <div className="px-3 py-4 text-center text-muted">
                                  <i className="bi bi-inbox d-block mb-2" style={{ fontSize: '2rem', opacity: 0.3 }}></i>
                                  <span style={{ fontSize: '0.9rem' }}>No projects found</span>
                                </div>
                              )}
                            </div>
                          </div>,
                          document.body
                        )
                      )}
                    </div>
                  </div>
                </Col>
                
                <Col lg={4} md={8}>
                  <div>
                    <label className="form-label fw-semibold mb-2" style={{ color: '#495057', fontSize: '0.9rem' }}>
                      <i className="bi bi-person-badge me-2"></i>Filter by Role
                    </label>
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      <Button 
                        variant={selectedRole === 'developer' ? 'primary' : 'outline-primary'}
                        onClick={() => handleRoleSelect('developer')}
                        size="sm"
                        style={{ 
                          borderRadius: 8,
                          padding: '0.5rem 0.75rem',
                          fontSize: '0.85rem',
                          fontWeight: 500,
                          border: '2px solid'
                        }}
                      >
                        <i className="bi bi-code-slash me-1"></i>Dev
                      </Button>
                      <Button 
                        variant={selectedRole === 'designer' ? 'success' : 'outline-success'}
                        onClick={() => handleRoleSelect('designer')}
                        size="sm"
                        style={{ 
                          borderRadius: 8,
                          padding: '0.5rem 0.75rem',
                          fontSize: '0.85rem',
                          fontWeight: 500,
                          border: '2px solid'
                        }}
                      >
                        <i className="bi bi-palette me-1"></i>Design
                      </Button>
                      <Button 
                        variant={selectedRole === 'marketing' ? 'warning' : 'outline-warning'}
                        onClick={() => handleRoleSelect('marketing')}
                        size="sm"
                        style={{ 
                          borderRadius: 8,
                          padding: '0.5rem 0.75rem',
                          fontSize: '0.85rem',
                          fontWeight: 500,
                          border: '2px solid'
                        }}
                      >
                        <i className="bi bi-megaphone me-1"></i>Marketing
                      </Button>
                      {selectedRole && (
                        <Badge 
                          bg="secondary" 
                          style={{ 
                            padding: '0.4rem 0.75rem',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            borderRadius: 6
                          }}
                        >
                          {selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1)}
                        </Badge>
                      )}
                    </div>
                  </div>
                </Col>
                
                <Col lg={2} md={4}>
                  <Button 
                    variant="outline-danger" 
                    onClick={handleClearFilters}
                    className="w-100"
                    style={{
                      borderRadius: 10,
                      padding: '0.65rem',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      border: '2px solid #dc3545'
                    }}
                  >
                    <i className="bi bi-x-circle me-2"></i>Clear
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          
          {/* Results count */}
          <div className="mb-3" style={{ color: '#9E9E9E', fontSize: '0.88rem' }}>
            Showing {filteredProjects.length} of {projectsWithHours.length} projects
          </div>

          <Row>
            {filteredProjects.map((project) => (
              <Col md={12} lg={6} key={project.id} className="mb-4">
                <Card className="hover-card" style={{ border: '1px solid #EEEEEE', borderRadius: 14, overflow: 'hidden' }}>
                  <Card.Header className="d-flex justify-content-between align-items-center" style={{
                    background: 'linear-gradient(135deg, #E8E0F0, #D6E6F5)',
                    borderBottom: 'none', borderRadius: '14px 14px 0 0',
                    padding: '0.9rem 1.25rem'
                  }}>
                    <h5 style={{ fontWeight: 700, color: '#7C6FA0', marginBottom: 0, fontSize: '1rem' }}>{project.name}</h5>
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => toggleProjectExpand(project.id)}
                      aria-expanded={expandedProjects[project.id]}
                    >
                      <i className={`bi bi-chevron-${expandedProjects[project.id] ? 'up' : 'down'}`}></i>
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                      <div style={{ flex: 1, background: '#F0E6FF', borderRadius: 10, padding: '0.75rem 1rem' }}>
                        <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#7C6FA0', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Hours</div>
                        <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#2D2D2D', lineHeight: 1.2 }}>{formatHoursToHoursMinutes(project.hoursData?.totalHours || 0)}</div>
                        <div style={{ fontSize: '0.73rem', color: '#9E9E9E', marginTop: 2 }}>{formatDecimalHours(project.hoursData?.totalHours || 0)} decimal</div>
                      </div>
                      <div style={{ flex: 1, background: '#D4EDDA', borderRadius: 10, padding: '0.75rem 1rem' }}>
                        <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6BAF7B', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Total Cost</div>
                        <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#2D2D2D', lineHeight: 1.2 }}>₹{(project.hoursData?.totalCost || 0).toFixed(2)}</div>
                      </div>
                      <div style={{ flex: 1, background: '#D6E6F5', borderRadius: 10, padding: '0.75rem 1rem' }}>
                        <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6BA3C7', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Tasks</div>
                        <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#2D2D2D', lineHeight: 1.2 }}>{project.hoursData?.taskCount || 0}</div>
                      </div>
                    </div>
                    
                    {project.hoursData?.userDistribution?.length > 0 ? (
                      <div>
                        <h6 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#616161', marginBottom: '0.6rem' }}>Distribution by {selectedRole ? selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1) : 'User'}</h6>
                        <div style={{ overflowX: 'auto' }}>
                        <Table striped hover size="sm" style={{ fontSize: '0.85rem', marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th>User</th>
                              <th>Hours</th>
                              <th>Rate</th>
                              <th>Cost</th>
                              <th>Percentage</th>
                            </tr>
                          </thead>
                          <tbody>
                            {project.hoursData.userDistribution.map((user, index) => {
                              const percentage = project.hoursData.totalHours > 0 
                                ? (user.hours / project.hoursData.totalHours) * 100 
                                : 0;
                              
                              return (
                                <tr key={user.user_id}>
                                  <td>{user.user_name}</td>
                                  <td>
                                    {formatHoursToHoursMinutes(user.hours)}
                                    <div className="text-muted small">({formatDecimalHours(user.hours)})</div>
                                  </td>
                                  <td>₹{(user.hourly_rate || 0).toFixed(2)}/hr</td>
                                  <td className="text-success fw-bold">₹{(user.cost || 0).toFixed(2)}</td>
                                  <td>
                                    <div style={{ position: 'relative', minHeight: '38px', minWidth: '80px', marginTop: '8px' }}>
                                      <ProgressBar 
                                        now={percentage} 
                                        variant={getProgressBarVariant(index)}
                                        style={{ height: '22px', marginBottom: '6px' }}
                                      />
                                      <div style={{
                                        position: 'absolute',
                                        top: '28px',
                                        left: 0,
                                        right: 0,
                                        fontSize: '0.72rem',
                                        fontWeight: 700,
                                        color: '#2D2D2D',
                                        textAlign: 'center',
                                        zIndex: 2
                                      }}>
                                        {typeof percentage === 'number' && !isNaN(percentage) ? percentage.toFixed(1) : '0.0'}%
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                        </div>
                      </div>
                    ) : (
                      <Alert variant="info">
                        No hours logged for this project yet.
                      </Alert>
                    )}
                    
                    {expandedProjects[project.id] && (
                      <div className="mt-3 border rounded p-2">
                        <h6 className="border-bottom pb-2 mb-3">Project Resources</h6>
                        <ProjectFiles projectId={project.id} />
                        <ProjectUrls projectId={project.id} />
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
          
          {projectsWithHours.length === 0 && (
            <Alert variant="info">
              No projects found. Create projects to track their hours.
            </Alert>
          )}
        </>
      )}

    </Container>
    </div>
  );
};

export default ProjectHours;
